/**
 * My Applications hub — default post-login landing page.
 * Shows services the current organization has access to.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

interface ServiceInfo {
  id: string;
  name: string;
}

function roleBadgeClass(role: string): string {
  switch (role) {
    case 'owner': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    case 'admin': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

export default function Apps() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, currentOrganization, organizations } = useAuth();
  const [services, setServices] = useState<ServiceInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const showWelcome = params.get('welcome') === '1';

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
    if (!authLoading && isAuthenticated && organizations.length === 0) {
      navigate('/onboarding', { replace: true });
    }
  }, [authLoading, isAuthenticated, organizations, navigate]);

  useEffect(() => {
    if (!currentOrganization) return;

    setLoading(true);
    setError(null);

    fetch(`${config.gatewayUrl}/v1/organizations/${currentOrganization.id}/services`, {
      credentials: 'include',
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.statusText))
      .then((data) => setServices(data.services || []))
      .catch(() => setError('Failed to load services.'))
      .finally(() => setLoading(false));
  }, [currentOrganization]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  const isOrgManager = currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Welcome banner (post-invitation-accept) */}
      {showWelcome && currentOrganization && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-5 py-4 flex items-start space-x-3">
          <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="font-medium text-green-800 dark:text-green-300">
              You've joined {currentOrganization.name}
            </p>
            {services.length > 0 && (
              <p className="text-sm text-green-700 dark:text-green-400 mt-0.5">
                You now have access to {services.length} service{services.length !== 1 ? 's' : ''} below.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">My Applications</h1>
          {currentOrganization && (
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-content-secondary">{currentOrganization.name}</span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${roleBadgeClass(currentOrganization.role)}`}>
                {currentOrganization.role}
              </span>
            </div>
          )}
        </div>
        {isOrgManager && (
          <Link
            to="/org"
            className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 flex items-center space-x-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Manage Org</span>
          </Link>
        )}
      </div>

      {/* Services */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner text="Loading services..." />
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      ) : services.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-content-primary mb-2">No services yet</h2>
          <p className="text-content-secondary text-sm max-w-sm mx-auto">
            {isOrgManager
              ? "Your organization hasn't been granted access to any services. Contact a platform administrator to enable services."
              : "Your organization hasn't been granted access to any services yet. Contact your organization administrator."}
          </p>
          {isOrgManager && (
            <Link to="/org" className="btn-primary mt-5 inline-block">
              Manage Organization
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {services.map((svc) => (
            <div key={svc.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-orbital-100 dark:bg-orbital-900/40 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orbital-600 dark:text-orbital-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-content-primary">{svc.name}</h3>
                    <p className="text-xs text-content-tertiary font-mono">{svc.id}</p>
                  </div>
                </div>
                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                  Active
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
